import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EditUserInfoContext } from '@/containers/pages';
import { defaultLang } from '@/entities/i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/redux/redux';
import { useUpdateInfo } from '@/hooks/users/useUpdateUser';
import { useUpdateSettingMutation } from '@/entities/admin-app/user/api';
import { userApi } from '@/entities/admin-app/user/api';
import { useActions } from '@/hooks/redux/action';
import { extractErrorCode } from '@/lib/utils/extractFetchErrors';
import { generateErrorMessage } from '@/lib/utils/generateErrorMessage';
import { ProfileSectionEnum, ProfileUpdateTypeEnum } from '@/types/enums/users';
import { showNotification } from '@mantine/notifications';
import i18n from '@/entities/i18next';

interface FormValues {
  language: string;
  mainTimezone: string;
  secondTimezone: string;
  checkbox: boolean;
}

export const useProfileLocaleTimezone = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setTimezoneSettings } = useActions();
  const { userInfoCommon, currentId } = useContext(EditUserInfoContext);
  const { id } = useParams();

  const { Settings } = useAppSelector((state) => state.userInfo);
  const myUserId = useAppSelector((state) => state.userInfo.User?.Id);
  const Timezone = Settings?.Timezone;

  const isCurrentUser = myUserId === Number(id);
  const language = useMemo(() => userInfoCommon?.Culture, [userInfoCommon]);

  const [updateInfo] = useUpdateSettingMutation();
  const { saveUpdate, isLoading: isLoadingUserUpdate } = useUpdateInfo({ isSilent: true });
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormValues>({
    validateInputOnChange: true,
    initialValues: {
      language: language || defaultLang,
      checkbox: isCurrentUser
        ? Timezone?.SecondEnabled ?? false
        : Boolean(userInfoCommon?.TimeZone?.SecondEnabled),
      mainTimezone: isCurrentUser
        ? String(Timezone?.Id ?? 0)
        : String(userInfoCommon?.TimeZone?.Id ?? 0),
      secondTimezone: isCurrentUser
        ? String(Timezone?.SecondId ?? 0)
        : String(userInfoCommon?.TimeZone?.SecondId ?? 0)
    }
  });

  useEffect(() => {
    if (userInfoCommon) {
      form.setValues({
        language: userInfoCommon.Culture || defaultLang,
        checkbox: userInfoCommon.TimeZone?.SecondEnabled ?? false,
        mainTimezone: String(userInfoCommon.TimeZone?.Id ?? 0),
        secondTimezone: String(userInfoCommon.TimeZone?.SecondId ?? 0)
      });
      form.resetDirty();
    }
  }, [userInfoCommon]);

  const handleSave = async () => {
    if (!userInfoCommon) return;

    const data = form.getValues();
    setIsLoading(true);

    try {
      // Проверяем, что изменилось
      const languageChanged = data.language !== userInfoCommon.Culture;
      const timezoneChanged =
        Number(data.mainTimezone) !==
          (isCurrentUser ? Timezone?.Id : userInfoCommon.TimeZone?.Id) ||
        data.checkbox !==
          (isCurrentUser ? Timezone?.SecondEnabled : userInfoCommon.TimeZone?.SecondEnabled) ||
        Number(data.secondTimezone) !==
          (isCurrentUser ? Timezone?.SecondId : userInfoCommon.TimeZone?.SecondId);

      // Сохранение языка (только если изменился)
      if (languageChanged) {
        await saveUpdate({
          Type: ProfileUpdateTypeEnum.Language,
          Data: {
            [ProfileSectionEnum.Language]: data.language as any,
            Id: currentId
          }
        });
        // Обновление языка в i18n
        i18n.changeLanguage(data.language);
      }

      // Сохранение таймзоны (только если изменилась)
      if (timezoneChanged) {
        if (!isCurrentUser) {
          await saveUpdate({
            Type: ProfileUpdateTypeEnum.TimeZone,
            Data: {
              [ProfileSectionEnum.TimeZone]: {
                ...userInfoCommon.TimeZone,
                Id: Number(data.mainTimezone) || 0,
                SecondEnabled: data.checkbox || false,
                SecondId: Number(data.secondTimezone) || 0
              },
              Id: currentId
            }
          });
        } else {
          if (!Settings) return;
          const res = await updateInfo({
            ...Settings,
            Timezone: {
              ...Settings.Timezone,
              Id: Number(data.mainTimezone) || 0,
              SecondEnabled: data.checkbox ?? false,
              SecondId: Number(data.secondTimezone) || 0
            }
          });

          if ('error' in res) {
            throw res.error;
          }

          setTimezoneSettings({
            ...Settings?.Timezone,
            Id: Number(data.mainTimezone) || 0,
            SecondEnabled: data.checkbox ?? false,
            SecondId: Number(data.secondTimezone) || 0
          });
        }
      }

      dispatch(userApi.util.invalidateTags(['Settings']));

      showNotification({
        title: t('saved'),
        message: '',
        color: 'green'
      });

      form.resetDirty();
    } catch (error) {
      const codeError = extractErrorCode(error);

      showNotification({
        title: generateErrorMessage(codeError),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading: isLoading || isLoadingUserUpdate,
    handleSave,
    isCurrentUser
  };
};
